import { EventCode, trackInternalEvent } from '@rio/tracking'
import { parse as parseQuery } from 'query-string'
import find from 'lodash/find'
import getByPath from 'lodash/get'
import snakeCase from 'lodash/snakeCase'
import isEmpty from 'lodash/isEmpty'
import formatISO from 'date-fns/formatISO'
import parseISO from 'date-fns/parseISO'
import isAfter from 'date-fns/isAfter'
import { Optional, PrefillResumeOptions, Resume, ResumeExample } from 'packages/types'
import {
  all,
  put,
  call,
  take,
  takeLatest,
  takeEvery,
  select,
  delay,
  fork,
} from 'redux-saga/effects'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { CallBackProps } from 'react-joyride'
import { i18n } from 'builder/utils/i18n'
import { apiClient, baseClient } from 'builder/modules/apiClient'
import { TemplateName } from 'builder/components/Helper/constants'
import { selectors as appSelectors, actions as initActions } from 'builder/modules/init/initModule'
import { actions as renderingActions } from 'builder/modules/rendering'
import { actions as panelActions } from 'builder/modules/panel'
import { actions as userActions, selectors as userSelectors, UserData } from 'builder/modules/user'
import {
  BuilderTourParams,
  SnackbarTypes,
  actions as uiActions,
  selectors as uiSelectors,
} from 'builder/modules/ui'
import { createInterceptor, needOnline, InterceptionHelper } from 'builder/modules/interceptors'
import { navigate } from 'builder/modules/navigate'
import PreviewReloadService from 'builder/services/PreviewReloadService'
import ErrorLogger from 'builder/services/ErrorLogger'
import AnchorManager from 'builder/services/AnchorManager'
import { generateRandomId } from 'builder/utils/generateRandomId'
import { isTruthy } from 'builder/utils/richTextUtils'
import { matchMediaQueries } from 'builder/utils/matchMediaQueries'
import { ANALYTICS_SECTION_NAME } from 'builder/components/AiFeatures/AiWorkExperience'
import { AppConfig, selectors as initSelectors } from 'builder/modules/init'
import {
  AIResumeDraftEventLabels,
  AIResumeDraftEvents,
  AIResumeLinkedInSteps,
  FetchStatuses,
  GenerateResumeStatus,
  GenerateAiSummaryStatus,
  ResumeUploadStep,
  PrefillResumeEvents,
  ResumeUploadStatus,
  ResumeValidationStatus,
  SpeechToTextStatus,
  UseExampleEventLabels,
  UseExampleEvents,
  VoiceInputErrors,
  AiSummaryGenerateType,
} from '../constants'
import { createImageUrlFormatter } from '../../../../packages/cloudflare-image/image-url'
import { Store } from '../store'
import { selectors } from './resumeEditorSelectors'
import { SectionNames } from './sectionNames'
import { StartResumeUpdatePayload, actions, CheckResumeScorePayload } from './resumeEditorActions'
import { mergeResumeHobbies } from './mergeResumeHobbies'
import {
  PrefillStatuses,
  QuestionnaireData,
  JobPostingAPIData,
  HighlightedElementData,
  AISummary,
  AvailableAISuggestions,
  ResumeScoreAPIData,
  AutoTailoringOpenedModalType,
  ResumeOpenedCard,
  ResumeOptimizerSection,
  CreateResumeWithLinkedInSteps,
  AutoTailoringFromPublicJobBoardStep,
  UseExample,
  BriefExampleCategory,
  CreateJobPostingResponse,
  AutoTailoringFromPublicJobBoard,
  FetchScoreResponse,
  AutoTailoringMode,
} from './types'
import { formatAIResumeQuestionnaireData } from './sagaUtils'
import {
  createAutoTailoringUrlParams,
  getCardFieldHighlightId,
  getCardHighlightId,
  joyRideSpotlightPositionInModalCssFix,
} from './utils'
import { getEmptyFieldName } from './aiProfileRules'
import {
  AFTER_USE_EXAMPLE_HIGHLIGHT_ID,
  AI_PROFILE_SUMMARY_ANALYTICS_LABEL,
  AI_WORK_EXPERIENCE_ANALYTICS_LABEL,
  EXAMPLES_LIST_PREVIEW_IMAGE_WIDTH_PX,
  SECTIONS_FOR_AUTO_TAILORING,
} from './constants'
import { isEmptyField } from './resumeScoreRules/sectionRule'
import { isDataMissedForAutoTailoring, isEmptySection } from './autoTailoringRules'
import { initialJobPostingAPIData } from './resumeEditorReducer'
const DEBOUNCE_DELAY = 1000
const RESUME_STORAGE_KEY = 'OFFLINE_RESUME_DATA'

const fixSectionsOrder = ({ customSections, sectionsOrder: sectionIds }: Resume) => {
  const missingCustomSections: string[] = []

  customSections.forEach(section => {
    const currentId = `custom:${section.externalId}`
    if (sectionIds.includes(currentId)) return
    missingCustomSections.push(currentId)
  })

  return [
    ...sectionIds.filter(sectionId => {
      const matches = sectionId.match(/custom:(\d+)/)
      if (!matches) return true
      return customSections.some(el => el.externalId === matches[1])
    }),
    ...missingCustomSections,
  ]
}

const saveResumeLocally = (resume: Resume) => {
  const updatedResume = { ...resume, updatedAt: formatISO(Date.now()) }
  localStorage.setItem(`${RESUME_STORAGE_KEY}_${resume.id}`, JSON.stringify(updatedResume))

  return updatedResume
}

const deserializeResume = (resume: Resume) => ({
  ...resume,
  sectionTitles: resume.sectionTitles ?? {},
  sectionsOrder: fixSectionsOrder(resume),
})

// polling saga for prefilling status
// checks if resume is prefilling from Top-resume if user used the prefill link
function* fetchResumePrefillStatusSaga(id: number) {
  // for showing prefilling popup everytime
  yield put(actions.setPrefillStatus(PrefillStatuses.scheduled))

  // refresh status with 5-second timer
  while (true) {
    yield delay(5000)

    const {
      data: { status },
    } = yield call(apiClient.get, `/resumes/${id}/prefill/status`)

    yield put(actions.setPrefillStatus(status))

    if (status === PrefillStatuses.done) {
      yield put(actions.fetchResumeRequest({ id }))
      const { payload: resume } = yield take(actions.fetchResumeSuccess)
      PreviewReloadService.reloadResume(resume, true)
      return
    }
  }
}

export function* fetchJobPostingData({ payload }: { payload: { resumeId?: number } }) {
  yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.loading))
  try {
    const {
      status,
      data: { success, jobDetails, resume },
    } = yield call(baseClient.post, `/job-postings/fetch-job-posting`, null, {
      params: {
        resume_id: payload.resumeId,
      },
    })
    if (status === 200 && success) {
      yield put(
        actions.setJobPostingDataForAutoTailoring({
          jobPostingId: jobDetails.id,
          employerName: resume.employerName,
          suggestedJobTitle: resume.jobTitle,
          averageScore: resume.averageScore,
          keywords: jobDetails.keywords || [],
        }),
      )
    }
    yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.loaded))
  } catch (err) {
    yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.failed))
  }
}

export function* fetchResumeOptimizerData({ payload }: { payload: { resumeId?: number } }) {
  yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.loading))
  try {
    const {
      status,
      data: { success, jobDetails, resume },
    } = yield call(baseClient.post, `/job-postings/fetch-job-posting`, null, {
      params: {
        resume_id: payload.resumeId,
      },
    })

    if (status === 200 && success) {
      const apiResponse = {
        isManagementJob: jobDetails.currentJobIsManagement === 't',
        jobPostingId: jobDetails.id,
        employerName: resume.employerName,
        recommendedJobTitle: resume.jobTitle,
        visibleJobTitle: jobDetails.jobTitle,
        recommendedSkills: jobDetails.skills,
        visibleSkills: jobDetails.skills,
        jobPostingLink: jobDetails.inputUrl,
        resumeScore: resume.score,
        apiScore: resume.responseApiScore,
        averageScore: resume.averageScore,
        keywords: jobDetails.keywords || [],
        recommendedKeywords: resume.recommendedKeywords || [],
      }
      yield put(actions.setTemporaryJobPostingAPIData(apiResponse))
      yield put(actions.saveJobPostingAPIData())
      yield put(
        actions.setResumeJobDetails({
          jobTitle: resume.jobTitle,
          employerName: resume.employerName,
        }),
      )
      yield put(
        actions.setJobPostingDataForAutoTailoring({
          jobPostingId: jobDetails.id,
          employerName: resume.employerName,
          suggestedJobTitle: resume.jobTitle,
          averageScore: resume.averageScore,
          keywords: jobDetails.keywords || [],
        }),
      )
      yield put(actions.refreshAISuggestions())
      yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.loaded))
    } else {
      yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.loaded))
      yield put(actions.resetJobPostingAPIData())
    }
  } catch (err) {
    const error = err as { response: { data: { message: string } } }
    ErrorLogger.log(error)

    yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.failed))
    yield put(
      uiActions.setSnackBarOpen({
        status: true,
        type: SnackbarTypes.failure,
        text:
          'Could not fetch AI Suggestions due to following error: ' +
          (error instanceof Error ? error.message : String(error)),
      }),
    )
    yield delay(1000)
    yield put(uiActions.setSnackBarOpen({ status: false, type: SnackbarTypes.failure }))
  }
}

export function* updateResumeOptimizerDataSaga({
  payload,
}: {
  payload: { jobPostingId?: number; resumeId?: number }
}) {
  yield put(actions.refreshAISuggestions())
  yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.loading))
  try {
    const {
      status,
      data: { jobDetails, resume },
    } = yield call(baseClient.post, `/job-postings/update-score`, null, {
      params: {
        job_posting_id: payload.jobPostingId,
        resume_id: payload.resumeId,
      },
    })

    if (status === 200) {
      const resumeData: Resume = yield select(selectors.resume)
      const jobPostingAPIData: JobPostingAPIData = yield select(selectors.jobPostingAPIData)
      const suggestedSkills = jobDetails.skills
      const currentVisibleSkills = jobPostingAPIData.visibleSkills ?? []
      const currentResumeSkills = (resumeData.skills ?? [])
        .map(item => (item.skill ? item.skill.trim() : ''))
        .filter(Boolean)
      // Only include new skills from the suggestions
      const newSkills = suggestedSkills.filter(
        (skill: string) =>
          !(currentResumeSkills.includes(skill) || currentVisibleSkills.includes(skill)),
      )
      const updatedSkills = [...currentVisibleSkills, ...newSkills]
      const apiResponse = {
        isManagementJob: jobDetails.currentJobIsManagement === 't',
        jobPostingId: jobDetails.id,
        employerName: jobDetails.companyName,
        recommendedJobTitle: jobDetails.jobTitle,
        visibleJobTitle: jobDetails.jobTitle,
        recommendedSkills: jobDetails.skills,
        visibleSkills: updatedSkills,
        jobPostingLink: jobDetails.inputUrl,
        resumeScore: resume.score,
        apiScore: resume.responseApiScore,
        averageScore: resume.averageScore,
        keywords: jobDetails.keywords || [],
        recommendedKeywords: resume.recommendedKeywords || [],
      }
      yield put(actions.setTemporaryJobPostingAPIData(apiResponse))
      yield put(actions.saveJobPostingAPIData())
      yield put(actions.refreshAISuggestions())
      yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.loaded))
    } else {
      yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.failed))
    }
  } catch (error) {
    yield put(actions.setFetchJobPostingDataStatus(FetchStatuses.failed))
    ErrorLogger.log(error)
  }
}

function* fetchResumeSaga({ payload }: ReturnType<typeof actions.fetchResumeRequest>) {
  try {
    // clear prefill resume modal states
    yield put(actions.setResumeValidationStatus(ResumeValidationStatus.notStarted))
    yield put(actions.setResumeUploadStatus(ResumeUploadStatus.notStarted))
    const isPrefillable = parseQuery(window.location.search).prefillable
    const { status: prefillingStatus } = yield select(selectors.prefill)
    const originalResume: Resume | null = yield select(selectors.originalResume)
    const { id } = payload
    const { data: resume } = yield call(apiClient.get, `/resumes/${id}`)
    const currentRemoteResume = mergeResumeHobbies(deserializeResume(resume)) as Resume
    const originalResumeId = currentRemoteResume.originalResumeId
    const { features } = yield select((state: Store) => initSelectors.config(state))
    const isResumeOptimizerFeatureOn = features?.resumeOptimizer

    const storageKey = `${RESUME_STORAGE_KEY}_${id}`
    const storageValue = localStorage?.getItem(storageKey)
    const localResume = storageValue ? JSON.parse(storageValue) : null
    const config: AppConfig = yield select(initSelectors.config)

    if (originalResumeId && originalResumeId !== originalResume?.id) {
      const { data } = yield call(apiClient.get, `/resumes/${originalResumeId}`)
      yield put(actions.setOriginalResume(data))
    }

    // if we have a local resume, check date, send resume to server
    // and remove from local storage
    if (localResume) {
      const processedLocalResume = mergeResumeHobbies(deserializeResume(localResume)) as Resume
      const shouldUseLocalData = isAfter(
        parseISO(processedLocalResume.updatedAt || ''),
        parseISO(currentRemoteResume.updatedAt || ''),
      )
      if (shouldUseLocalData) {
        yield put(actions.fetchResumeSuccess(processedLocalResume))
        yield put(actions.startResumeUpdate())
      }
      localStorage.removeItem(storageKey)
    }

    yield put(renderingActions.checkDocument(currentRemoteResume))
    yield take(renderingActions.checkDocumentComplete)

    const { Rirekisho, Shokumukeirekisho } = TemplateName

    const country: { locale: string } = yield select(selectors.country)
    const isJapaneseBuilderon = country.locale === 'ja-JP'

    const { createdAt, updatedAt } = currentRemoteResume

    if (isJapaneseBuilderon && currentRemoteResume.template === Shokumukeirekisho) {
      yield put(actions.fetchResumeSuccess(currentRemoteResume))
    } else if (isJapaneseBuilderon && createdAt === updatedAt) {
      yield put(actions.fetchResumeSuccess({ ...currentRemoteResume, template: Rirekisho }))
    } else {
      // in all other conditions fetches the previous selected template in resume view on render
      yield put(actions.fetchResumeSuccess(currentRemoteResume))

      if (config.features?.rbpAiReview) {
        yield put(actions.getLatestAiResumeReview())
      }

      if (isResumeOptimizerFeatureOn) {
        try {
          yield call(fetchResumeOptimizerData, {
            payload: { resumeId: payload.id },
          })

          // this needs to get correct 'recommendedKeywords' value
          const jobPostingId: number = yield select(selectors.tailoredJobPostingId)
          if (jobPostingId) {
            yield call(updateResumeOptimizerDataSaga, {
              payload: { resumeId: payload.id, jobPostingId },
            })
          }
        } catch (error) {
          ErrorLogger.log(error)
        }
      }
    }

    if (isPrefillable && prefillingStatus === PrefillStatuses.notAsked)
      yield call(fetchResumePrefillStatusSaga, id)
  } catch (error) {
    // Customize the error message for 404
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      const axiosError = error as AxiosError
      axiosError.message = `${axiosError.message}. The requested resume might not exist.`
      ErrorLogger.log(axiosError)
    } else ErrorLogger.log(error)

    if (error instanceof Error) {
      yield put(actions.fetchResumeFail(error))
    }
  }
}

function* updateLocalResumeSaga() {
  const resume: Resume = yield select(selectors.resume)
  const updatedResume: Resume = yield call(saveResumeLocally, resume)
  yield delay(300) // for spinner
  yield put(actions.updateResumeSuccess(updatedResume))
}

function* updateRemoteResumeSaga({ payload = {} }: { payload: StartResumeUpdatePayload }) {
  try {
    const { debounce = false } = payload

    if (debounce === true) yield delay(DEBOUNCE_DELAY)
    if (typeof debounce === 'number') yield delay(debounce)

    const resume: Resume = yield select(selectors.resume)
    const { points: score } = yield select(selectors.scoreSuggestions)
    const url = `/resumes/${resume.id}`

    let { data } = yield call(apiClient.patch, url, { ...resume, score })
    const updatedResume = deserializeResume(data)

    yield put(renderingActions.checkDocument(updatedResume))
    yield take(renderingActions.checkDocumentComplete)

    yield put(actions.updateResumeSuccess(updatedResume))

    const refresh = payload?.name && ['template', 'color'].includes(payload?.name)
    PreviewReloadService.reloadResume(updatedResume, refresh)

    const jobPostingId: number = yield select(selectors.tailoredJobPostingId)
    const isAISuggestionsPopupOpen: boolean = yield select(selectors.openedAISuggestionsPopup)

    if (jobPostingId && !isAISuggestionsPopupOpen) {
      yield call(updateResumeOptimizerDataSaga, {
        payload: { jobPostingId, resumeId: resume.id },
      })
    }
  } catch (error) {
    ErrorLogger.log(error)

    // if something goes wrong when the user edits the resume,
    // save it to localStorage. It will be synced on page reload or
    // next edit with an internet connection on.
    yield call(
      updateLocalResumeSaga as unknown as {
        context: unknown
        fn: (this: unknown, ...args: unknown[]) => unknown
      },
      { payload },
    )
  }
}

function* updateResumeSaga(action?: ReturnType<typeof actions.startResumeUpdate>) {
  const isOnline: boolean = yield select(appSelectors.isOnline)

  // if there is no internet connection, save resume in localStorage
  yield call(
    (isOnline ? updateRemoteResumeSaga : updateLocalResumeSaga) as unknown as {
      context: unknown
      fn: (this: unknown, ...args: unknown[]) => unknown
    },
    action,
  )
}

function* prefillResumeSaga({ payload = {} }: ReturnType<typeof actions.prefillResume>) {
  try {
    const { documentId, exampleId } = payload

    const { data } = yield call(apiClient.post, `/resumes/${documentId}/prefill/${exampleId}`)

    if (data.success) {
      yield put(actions.updateResumeSuccess(data.resume))
      yield put(uiActions.setPrefillModalDocumentId(null))
    } else {
      yield put(uiActions.setWarningSnackbarOpen({ timeout: 5000, text: data.errorMessage }))
    }
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.updateResumeFail())
    yield put(uiActions.setPrefillModalDocumentId(null))
  }
}

function* startResumeUpdateSaga({ payload = {} }) {
  try {
    yield put(actions.startResumeUpdate(payload))
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function* updateTemplateSaga({ payload }: ReturnType<typeof actions.updateTemplate>) {
  try {
    const { id } = payload
    const templates: Store['generalEditor']['resumeTemplates'] = yield select(selectors.templates)
    const template = find(templates, { id })
    const color = getByPath(template, 'settings.color.default', null)

    yield put(actions.updateSimpleField({ name: 'color', value: color, debounce: true }))
    yield put(actions.updateSimpleField({ name: 'spacing', value: 0, debounce: true }))
    yield put(actions.updateSimpleField({ name: 'template', value: id }))
    yield put(actions.updateSimpleField({ name: 'templateConfig', value: template }))

    yield put(
      actions.updateSimpleField({
        name: 'ableForSkillCategories',
        value: template?.supportsSkillCategories ?? false,
      }),
    )
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function* addCardSaga({ payload }: ReturnType<typeof actions.addCard>) {
  try {
    const { sectionName, cardId, options } = payload
    yield put(actions.startResumeUpdate(payload))

    if (options && options.scrollIntoViewport) {
      AnchorManager.scrollToAnchor(cardId)
    }

    if (
      sectionName &&
      typeof sectionName !== 'number' &&
      Object.values(SectionNames).some(name => name === sectionName)
    ) {
      trackInternalEvent(`add_resume_${snakeCase(sectionName)}_card` as EventCode)
    } else {
      trackInternalEvent(`add_resume_custom_card`)
    }
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function* addCustomSectionSaga({ payload }: ReturnType<typeof actions.addCustomSection>) {
  try {
    const { externalId, options } = payload
    yield put(actions.setCustomSection(payload))
    yield put(actions.startResumeUpdate(payload))

    if (options?.scrollIntoViewport) {
      AnchorManager.scrollToAnchor(externalId)
    }
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function* uploadImageSaga({ payload = {} }: ReturnType<typeof actions.uploadPhoto>) {
  try {
    yield put(actions.changeEditorState({ isUploadingPhoto: true, isSyncing: true }))

    const { file, transform = {}, processedFile } = payload

    const resume: Resume = yield select(selectors.resume)
    const action = file ? 'photo' : 'transform'
    const url = `/resumes/${resume.id}/${action}`

    // POST /app/resumes/:id/photo
    // FormData photo:file, transform:json
    const form = new FormData()
    if (file) form.append('photo', file)
    if (processedFile) form.append('processed', processedFile)
    form.append('transform', JSON.stringify(transform))

    const { data } = yield call(apiClient.post, url, form)
    const updatedResume = deserializeResume(data)

    yield put(
      actions.changeEditorState({
        isUploadingPhoto: false,
        isSyncing: false,
        resume: updatedResume,
      }),
    )

    PreviewReloadService.reloadResume(updatedResume, false)

    // For the career.io MVP we connected user profile picture to their resume avatar.
    // So must refresh account data after user uploads a new picture to the resume avatar editor.
    yield put(userActions.fetchUserRequest({ silent: true }))
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.changeEditorState({ isUploadingPhoto: false, isSyncing: false }))
  }
}

function* deleteImageSaga() {
  try {
    yield put(actions.changeEditorState({ isSyncing: true }))

    const resume: Resume = yield select(selectors.resume)
    const url = `/resumes/${resume.id}/photo`

    // DELETE /app/resumes/:id/photo

    const { data } = yield call(apiClient.delete, url)
    const updatedResume = deserializeResume(data)

    yield put(
      actions.changeEditorState({
        isSyncing: false,
        resume: updatedResume,
      }),
    )

    PreviewReloadService.reloadResume(updatedResume, false)

    // TODO: Remove this side-effect after releasing the updated account settings page
    yield put(userActions.fetchUserRequest({ silent: true }))
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.changeEditorState({ isSyncing: false }))
  }
}

function* internetConnectionStatusChangedSaga({
  payload,
}: ReturnType<typeof initActions.setInternetConnectionStatus>) {
  const { isOnline } = payload
  const resume: Resume = yield select(selectors.resume)
  if (!resume) return
  const storageKey = `${RESUME_STORAGE_KEY}_${resume.id}`

  // if the connection switched on, sync remote resume with
  // the local version
  if (isOnline && localStorage.getItem(storageKey)) {
    yield put(actions.fetchResumeRequest({ id: resume.id }))
  }
}

function* handleResumeOptimizerErrorFlow({ payload }: { payload: CheckResumeScorePayload }) {
  if (payload.jobPostingManualInput && payload.jobPostingManualInput.companyName) {
    yield put(actions.setShowManualInputScreen(false))
    yield put(actions.setFetchResumeScoreStatus(FetchStatuses.failed))
  } else {
    yield put(actions.setShowManualInputScreen(true))
    yield put(actions.setFetchResumeScoreStatus(FetchStatuses.notAsked))
  }
}

export function* checkResumeScoreSaga({ payload }: { payload: CheckResumeScorePayload }) {
  const { resumeId, jobPostingLink, jobPostingManualInput } = payload
  try {
    yield put(actions.setFetchResumeScoreStatus(FetchStatuses.loading))

    const createJobPostingResponse: AxiosResponse<CreateJobPostingResponse> = yield call(
      baseClient.post,
      '/tailored_resumes/create_job_posting',
      {
        ...(jobPostingManualInput
          ? { jobPosting: jobPostingManualInput }
          : { job_url: jobPostingLink }),
      },
    )

    const {
      data: {
        jobPosting: { id: jobPostingId, jobTitle, companyName, keywords },
      },
    } = createJobPostingResponse

    const response: AxiosResponse<FetchScoreResponse> = yield call(
      baseClient.post,
      '/tailored_resumes/fetch_score',
      {
        job_posting_id: jobPostingId,
        resume_id: resumeId,
      },
    )

    const {
      status,
      data: { averageScore },
    } = response

    if (status === 200) {
      const apiResponse = {
        employerName: companyName,
        suggestedJobTitle: jobTitle,
        averageScore: averageScore,
        jobPostingId,
        keywords: keywords || [],
      }
      yield put(actions.resetResumeOptimizerManualInput())
      yield put(actions.setResumeScoreAPIResponse(apiResponse))
      yield put(actions.setJobPostingDataForAutoTailoring(apiResponse))
      yield put(actions.setFetchResumeScoreStatus(FetchStatuses.loaded))
      yield put(actions.setShowManualInputScreen(false))
    } else {
      yield call(handleResumeOptimizerErrorFlow, { payload: { jobPostingManualInput } })
    }
  } catch (error) {
    ErrorLogger.log(error)
    yield call(handleResumeOptimizerErrorFlow, { payload: { jobPostingManualInput } })
  }
}

interface ResumeTypes extends Resume {
  responseApiScore: number
  score: number
  recommendedKeywords: string[]
}

export function* saveJobPostingDataSaga({
  payload,
}: {
  payload: { jobPostingLink?: string; resumeId?: number; jobTitle?: string; companyName?: string }
}) {
  try {
    yield put(actions.setSaveJobPostingDataStatus(FetchStatuses.loading))
    const { averageScore, jobPostingId }: ResumeScoreAPIData = yield select(
      selectors.resumeScoreAPIData,
    )

    let response: {
      status: number
      errorMessage: string
      data: {
        success: string
        jobPosting: {
          currentJobIsManagement: string
          id: number
          companyName: string
          jobTitle: string
          skills: string[]
          inputUrl: string
          keywords: string[]
        }
        resume: ResumeTypes
      }
    } = yield call(baseClient.post, `/tailored_resumes/sync_resume_with_job_posting`, null, {
      params: {
        ...(!jobPostingId
          ? {
              job_url: payload.jobPostingLink,
            }
          : {}),
        resume_id: payload.resumeId,
        job_title: payload.jobTitle,
        company_name: payload.companyName,
        job_posting_id: jobPostingId,
        average_score: averageScore,
      },
    })

    yield delay(1000)

    const {
      status,
      data: { jobPosting, resume },
    } = response

    if (status === 200) {
      const apiResponse = {
        isManagementJob: jobPosting.currentJobIsManagement === 't',
        jobPostingId: jobPosting.id,
        employerName: resume.employerName,
        recommendedJobTitle: resume.jobTitle,
        visibleJobTitle: jobPosting.jobTitle,
        recommendedSkills: jobPosting.skills,
        visibleSkills: jobPosting.skills,
        jobPostingLink: jobPosting.inputUrl,
        resumeScore: resume.score,
        apiScore: resume.responseApiScore,
        averageScore: resume.averageScore,
        keywords: jobPosting.keywords || [],
        recommendedKeywords: resume.recommendedKeywords || [],
      }
      yield put(actions.setTemporaryJobPostingAPIData(apiResponse))
      yield put(
        actions.setResumeJobDetails({
          jobTitle: resume.jobTitle,
          employerName: resume.employerName,
        }),
      )

      yield put(actions.setSaveJobPostingDataStatus(FetchStatuses.loaded))
    } else {
      yield put(actions.setSaveJobPostingDataStatus(FetchStatuses.notAsked))
    }
  } catch (err) {
    const error = err as { response: { data: { message: string } } }
    ErrorLogger.log(error)
    if (
      error.response &&
      error.response.data &&
      error.response.data.message === 'No job posting found.'
    ) {
      yield put(actions.setSaveJobPostingDataStatus(FetchStatuses.loaded))
      // yield put(actions.resetJobPostingAPIData())
    } else {
      let errorString =
        'Oops! Something went wrong. We apologize for the inconvenience. Please try again later. If the issue persists, please contact support for assistance.'
      if (error.response.data.message === 'Network error') {
        errorString = `Oops! It appears there's a problem with your network connection. Please check your internet connection and try again.`
      }
      yield put(
        uiActions.setSnackBarOpen({
          status: true,
          type: SnackbarTypes.failure,
          text: errorString,
        }),
      )
      yield delay(3000)
      yield put(uiActions.setSnackBarOpen({ status: false, type: SnackbarTypes.failure }))
      yield put(actions.setSaveJobPostingDataStatus(FetchStatuses.notAsked))
    }
  }
}

export function* startResumeOptimizerProcessSaga({
  payload,
}: ReturnType<typeof actions.resetResumeOptimizerProcess>) {
  if (!payload?.skipScoreStatusReset) {
    yield put(actions.setFetchResumeScoreStatus(FetchStatuses.notAsked))
  }
  yield put(actions.setSaveJobPostingDataStatus(FetchStatuses.notAsked))

  const params = new URLSearchParams(window.location.search)
  const initialValue = params.get('job_url') || ''
  if (initialValue) {
    window.history.replaceState({}, '', `${window.location.origin}${window.location.pathname}`)
  }
  yield put(actions.setJobPostingLinkForModal(initialValue))
  yield put(actions.setShowManualInputScreen(false))
}

export function* startImprovingResumeSaga({
  payload,
}: ReturnType<typeof actions.startImprovingResume>) {
  yield call(saveJobPostingDataSaga, {
    payload: { ...payload },
  })

  yield put(actions.setOpenedAISuggestionsPopup(AvailableAISuggestions.jobTitle))
  yield put(actions.setIsImproveResumePanelOpen({ status: true }))

  const saveJobPostingDataStatus: string = yield select(selectors.saveJobPostingDataStatus)
  const autoTailoringOpenedModal: AutoTailoringOpenedModalType | null = yield select(
    selectors.autoTailoringOpenedModal,
  )

  if (saveJobPostingDataStatus === FetchStatuses.loaded) {
    yield put(actions.saveJobPostingAPIData())
    yield put(actions.refreshAISuggestions())
    yield put(actions.setOpenOptimizerModal(false))
  }

  if (
    saveJobPostingDataStatus === FetchStatuses.loaded &&
    autoTailoringOpenedModal === 'auto_tailoring'
  ) {
    yield put(actions.setAutoTailoringModalStep(null))
  }
}

export function* startImprovingResumeForEditorWithTabsSaga({
  payload,
}: ReturnType<typeof actions.startImprovingResumeForEditorWithTabs>) {
  yield call(saveJobPostingDataSaga, {
    payload: { ...payload },
  })

  const saveJobPostingDataStatus: string = yield select(selectors.saveJobPostingDataStatus)

  if (saveJobPostingDataStatus === FetchStatuses.loaded) {
    yield put(actions.saveJobPostingAPIData())
    yield put(actions.refreshAISuggestions())
    yield put(actions.resetResumeOptimizerProcess())
    yield put(actions.setEditorPanelType('editor'))
  }
}

export function* deleteJobPostingSaga({ payload }: ReturnType<typeof actions.deleteJobPosting>) {
  try {
    const { shouldRefreshPage = true } = payload
    yield put(actions.setDeleteJobPostingStatus(FetchStatuses.loading))
    const response: {
      status: number
      data: { success: string }
    } = yield call(baseClient.delete, `/job-postings/soft_delete`, {
      params: { id: payload.jobPostingId, resume_id: payload.resumeId },
    })

    const { status } = response

    if (status === 200) {
      if (shouldRefreshPage) {
        window.location.href = `/app/resumes/${payload.resumeId}/edit`
      } else {
        yield put(actions.setTemporaryJobPostingAPIData(initialJobPostingAPIData))
        yield put(actions.saveJobPostingAPIData())
        yield put(actions.refreshAISuggestions())
        yield put(actions.resetResumeOptimizerProcess())
      }
      yield put(actions.setDeleteJobPostingStatus(FetchStatuses.loaded))
    }
  } catch (err) {
    const error = err as { response: { data: { message: string } } }
    ErrorLogger.log(error)
    yield put(actions.setDeleteJobPostingStatus(FetchStatuses.failed))
    yield put(
      uiActions.setSnackBarOpen({
        status: true,
        type: SnackbarTypes.failure,
        text: error.response.data.message,
      }),
    )
    yield delay(1000)
    yield put(uiActions.setSnackBarOpen({ status: false, type: SnackbarTypes.failure }))
  }
}

export function* editJobDetailsSaga({ payload }: ReturnType<typeof actions.editJobDetails>) {
  try {
    const jobPostingId: number = yield select(selectors.tailoredJobPostingId)
    const response: {
      status: number
      data: { success: string; resume: { jobTitle: string; employerName: string } }
    } = yield call(baseClient.put, `/job-postings/edit-job-posting`, {
      job_title: payload.jobTitle,
      company_name: payload.companyName,
      ...(payload.jobPostingLink
        ? { job_url: payload.jobPostingLink }
        : { job_posting_id: jobPostingId }),
      resume_id: payload.resumeId,
    })

    const {
      status,
      data: {
        success,
        resume: { jobTitle, employerName },
      },
    } = response

    if (status === 200 && success) {
      const jobPostingAPIData: JobPostingAPIData = yield select(selectors.jobPostingAPIData)
      yield put(
        actions.setTemporaryJobPostingAPIData({
          ...jobPostingAPIData,
          recommendedJobTitle: jobTitle,
          employerName: employerName,
        }),
      )
      yield put(actions.saveJobPostingAPIData())
      yield put(actions.setResumeJobDetails({ jobTitle: jobTitle, employerName: employerName }))
      yield put(actions.refreshAISuggestions())
      yield put(actions.editJobDetailsStatus(FetchStatuses.loaded))
      yield delay(1000)

      yield put(actions.setIsEditJobDetails(false))
      yield put(actions.setIsClickedOnEditJobDetails(false))
      yield put(
        uiActions.setSnackBarOpen({
          status: true,
          type: SnackbarTypes.success,
          text: payload.successMessage,
        }),
      )
      yield put(actions.editJobDetailsStatus(FetchStatuses.notAsked))
      yield delay(2000)
      yield put(uiActions.setSnackBarOpen({ status: false }))
    }
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.editJobDetailsStatus(FetchStatuses.failed))
    yield put(
      uiActions.setSnackBarOpen({
        status: true,
        type: SnackbarTypes.failure,
        text: 'Looks like we couldn’t save the changes. Please check your internet connection and try again.',
      }),
    )
    yield delay(3000)
    yield put(actions.setIsEditJobDetails(false))
    yield put(actions.editJobDetailsStatus(FetchStatuses.notAsked))
    yield put(uiActions.setSnackBarOpen({ status: false, type: SnackbarTypes.failure }))
  }
}

function* performAfterPrefillAction(option: PrefillResumeOptions) {
  const resume: Resume = yield select(selectors.resume)
  const { isTablet } = matchMediaQueries()

  const { enabled }: AutoTailoringFromPublicJobBoard = yield select(
    selectors.autoTailoringFromPublicJobBoard,
  )

  if (enabled) {
    yield put(
      actions.setAutoTailoringForPublicJobBoard({
        showPrefillBanner: true,
      }),
    )
  }

  if (!resume.data?.stepByStepFlow?.currentStep) {
    return
  }

  yield put(
    actions.updateSimpleField({
      name: 'data',
      value: {
        stepByStepFlow: {
          currentStep: null,
        },
        selectedPrefillOption: option,
      },
      debounce: false,
    }),
  )

  yield take(actions.updateResumeSuccess)
  const { features }: AppConfig = yield select(initSelectors.config)

  if (enabled && features?.isNewBuilderEnabled) {
    yield put(actions.setIsCustomizeModalOpen(true))
    trackInternalEvent('open_full_screen_resume_preview')
    return
  }

  switch (option) {
    case 'uploadResume':
    case 'useExample':
      if (isTablet) {
        yield put(actions.setIsCustomizeModalOpen(true))
        trackInternalEvent('open_full_screen_resume_preview')
      }
      break
  }
}

export function* parseAndSavePrefilledResumeSaga({
  payload,
}: ReturnType<typeof actions.parseAndSavePrefilledResume>) {
  const { resumeId, selectedResume, keepResumeData = false } = payload
  yield put(actions.setIsSavingPrefilledResume(true))
  try {
    const formData = new FormData()
    formData.append('file', selectedResume)
    formData.append('resume_id', `${resumeId}`)

    const response: {
      status: number
      data: { success: boolean; error: string }
    } = yield call(apiClient.post, `/resume-parser/parse-resume`, formData)

    if (response.data.success) {
      yield put(actions.setResumeUploadStatus(ResumeUploadStatus.uploaded))
      yield put(actions.setResumeUploadSuccessStatus(true))
      yield put(
        actions.fetchResumeRequest({
          id: resumeId,
          keepResumeData,
        }),
      )
      yield take(actions.fetchResumeSuccess)
      trackInternalEvent(PrefillResumeEvents.completeResumeParsing)
      yield call(performAfterPrefillAction, 'uploadResume')
    } else {
      yield put(actions.setResumeUploadStatus(ResumeUploadStatus.failed))
      yield put(
        actions.setPrefillResumeGenericError({
          error: response.data.error,
          errorSource: ResumeUploadStep.parsing,
        }),
      )
    }
  } catch (error) {
    yield put(actions.setResumeUploadStatus(ResumeUploadStatus.failed))
    if (error instanceof Error) {
      yield put(
        actions.setPrefillResumeGenericError({
          error: error.message,
          errorSource: ResumeUploadStep.parsing,
        }),
      )
    }
  }
  yield put(actions.setIsSavingPrefilledResume(false))
}

// AICoverLetter
export function* aiCoverLetterTailoredGenerateSaga({ payload }: { payload: { resumeId: number } }) {
  try {
    const response: {
      status: number
      data: { success: string; coverLetter: { id: number } }
    } = yield call(baseClient.post, `/job-postings/generate-cover-letter`, null, {
      params: {
        resume_id: payload.resumeId,
      },
    })

    const {
      status,
      data: { success, coverLetter },
    } = response

    if (status === 200 && success && coverLetter.id) {
      // To refresh the user attempts
      trackInternalEvent(`generate_cover_letter_succesfully`)
      yield put(userActions.fetchUserRequest({ silent: true }))
      window.location.href = `/app/cover-letters/${coverLetter.id}/edit`
    } else {
      yield put(actions.setIsClickedOnAiCVBanner(false))
      yield put(actions.setGenerateAICoverLetterStatus(FetchStatuses.failed))
    }
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.setGenerateAICoverLetterStatus(FetchStatuses.failed))
  }
}

// AICoverLetterGenerateUntailored
export function* generateAICVUntailoredSaga({
  payload,
}: ReturnType<typeof actions.generateAICVUntailored>) {
  const { resumeId } = payload
  try {
    yield call(saveJobPostingDataSaga, {
      payload: { ...payload },
    })
    yield put(actions.setOpenedAISuggestionsPopup(AvailableAISuggestions.jobTitle))

    const saveJobPostingDataStatus: string = yield select(selectors.saveJobPostingDataStatus)

    if (saveJobPostingDataStatus === FetchStatuses.loaded) {
      yield put(actions.saveJobPostingAPIData())
    }
    yield call(aiCoverLetterTailoredGenerateSaga, {
      payload: { resumeId },
    })
  } catch (error) {
    ErrorLogger.log(error)
  }
}

export function* fetchLinkedInProfileSaga({
  payload,
}: ReturnType<typeof actions.fetchLinkedInProfile>) {
  const { linkedInProfileURL } = payload
  try {
    yield put(actions.setLinkedInProfileFetchStatus(true))

    const { status, data } = yield call(
      apiClient.get,
      `/linkedin-data-profile/profile?linkedin_profile=${linkedInProfileURL}`,
    )

    if (status === 200 && !isEmpty(data)) {
      yield put(actions.setLinkedInProfileData({ ...data }))
      yield put(actions.setAIResumeLinkedInStep(AIResumeLinkedInSteps.linkedInProfile))
    } else {
      // handle error
      yield put(actions.setAIResumeLinkedInStep(AIResumeLinkedInSteps.linkedInError))
    }
  } catch (error) {
    // handle error
    yield put(actions.setAIResumeLinkedInStep(AIResumeLinkedInSteps.linkedInError))
    ErrorLogger.log(error)
  } finally {
    yield put(actions.setLinkedInProfileFetchStatus(false))
  }
}

export function* generateResumeFromLinkedInSaga({
  payload,
}: ReturnType<typeof actions.generateResumeFromLinkedIn>) {
  const { linkedInProfileURL, resumeId } = payload
  try {
    trackInternalEvent(AIResumeDraftEvents.startGenerating, {
      label: AIResumeDraftEventLabels.linkedInProfile,
    })
    yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.processing))

    const { data } = yield call(
      apiClient.patch,
      `/linkedin-data-profile/prefill-resume?linkedin_profile=${linkedInProfileURL}` +
        `&resume_id=${resumeId}&ai_enhanced=true`,
    )

    if (data) {
      trackInternalEvent(AIResumeDraftEvents.generateSuccessfully, {
        label: AIResumeDraftEventLabels.linkedInProfile,
      })
      yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.finished))
      yield put(actions.setShowAIResumeModal(false))
      yield put(
        actions.fetchResumeRequest({
          id: resumeId,
        }),
      )
      yield take(actions.fetchResumeSuccess)
      yield call(performAfterPrefillAction, 'createWithLinkedIn')
    } else {
      trackInternalEvent(AIResumeDraftEvents.generateError, {
        label: AIResumeDraftEventLabels.linkedInProfile,
      })
      yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.failed))
    }
  } catch (error) {
    trackInternalEvent(AIResumeDraftEvents.generateError, {
      label: AIResumeDraftEventLabels.linkedInProfile,
    })
    yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.failed))
  }
}

export function* generateResumeFromQuestionnaireSaga() {
  try {
    trackInternalEvent(AIResumeDraftEvents.startGenerating, {
      label: AIResumeDraftEventLabels.questionnaire,
    })
    yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.processing))
    const { firstName, lastName, id }: Resume = yield select(selectors.resume)
    const questionnaireData: QuestionnaireData = yield select(selectors.questionnaireData)
    const formattedPayload = formatAIResumeQuestionnaireData(questionnaireData)
    const {
      data: { success, resume },
    } = yield call(apiClient.put, `/ai-resume-draft/${id}`, {
      linkedinGenerated: false,
      questionnaireData: {
        name: `${firstName} ${lastName}`,
        ...formattedPayload,
      },
    })

    if (success) {
      trackInternalEvent(AIResumeDraftEvents.generateSuccessfully, {
        label: AIResumeDraftEventLabels.questionnaire,
      })
      yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.finished))
      yield put(actions.setShowAIResumeModal(false))
      yield put(actions.fetchResumeRequest({ id: resume.id }))
      yield take(actions.fetchResumeSuccess)
      yield call(performAfterPrefillAction, 'createWithAi')
    } else {
      trackInternalEvent(AIResumeDraftEvents.generateError, {
        label: AIResumeDraftEventLabels.questionnaire,
      })
      yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.failed))
    }
  } catch (error) {
    trackInternalEvent(AIResumeDraftEvents.generateError, {
      label: AIResumeDraftEventLabels.questionnaire,
    })
    yield put(actions.setGenerateResumeStatus(GenerateResumeStatus.failed))
  }
}

export function* generateResumeAiProfileSummarySaga() {
  try {
    yield put(actions.setGenerateAiProfileSummaryStatus(GenerateAiSummaryStatus.processing))

    const resume: Resume = yield select(selectors.resume)
    const { id } = resume
    const { actionType, includeJobPosting }: AISummary = yield select(selectors.aiProfile)
    const isProfileEmpty = isEmptyField(resume, SectionNames.profile)

    const { data, status } = yield call(apiClient.post, `/ai-profile-summary/${id}/summary`, {
      profile_summary: actionType === AiSummaryGenerateType.improve,
      include_job_posting: includeJobPosting,
    })

    if (status === 200 && data) {
      yield put(actions.setAiProfileSummaryText(data.data.summary))
      yield put(actions.setGenerateAiProfileSummaryStatus(GenerateAiSummaryStatus.finished))
      yield put(userActions.decrementAiProfileAttempts())
      yield put(userActions.fetchUserRequest({ silent: true }))

      trackInternalEvent('generate_successfully', {
        section: SectionNames.profile,
        label: AI_PROFILE_SUMMARY_ANALYTICS_LABEL,
        profile_empty: isProfileEmpty,
        mode: actionType,
      })

      if (isProfileEmpty) {
        yield put(actions.updateSimpleField({ name: 'profile', value: data.data.summary }))
        yield put(actions.setAIProfileEditorFieldAnimation(true))
        yield put(actions.setAIProfileResultContentAnimation(false))
      } else {
        yield put(actions.setIsAIProfileSummaryResultOpen({ status: true }))
      }
    }
  } catch (error) {
    yield put(actions.setGenerateAiProfileSummaryStatus(GenerateAiSummaryStatus.failed))
  }
}

export function* generateResumeAiWorkExperienceSaga({
  payload: { workExperienceId },
}: ReturnType<typeof actions.generateResumeAiWorkExperience>) {
  try {
    yield put(actions.setGenerateAiWorkExperienceStatus(GenerateAiSummaryStatus.processing))

    const resume: Resume = yield select(selectors.resume)
    const { id, workExperiences } = resume
    const workExperience = workExperiences.find(({ id }) => id === workExperienceId)
    const { actionType, includeJobPosting }: AISummary = yield select(selectors.aiWorkExperience)
    const isDescriptionEmpty = !isTruthy(workExperience?.description)

    const { data, status } = yield call(
      baseClient.post,
      `/tailored_resumes/${id}/generate_work_experience`,
      {
        includeDescription: actionType === AiSummaryGenerateType.improve,
        includeJobPosting: includeJobPosting,
        workExperienceId: workExperienceId,
      },
    )

    if (status === 200 && data) {
      const newDescription = data.workExperience[0]?.description

      yield put(actions.setAiWorkExperienceText(newDescription))
      yield put(actions.setGenerateAiWorkExperienceStatus(GenerateAiSummaryStatus.finished))
      yield put(userActions.decrementAiWorkExperienceAttempts())
      yield put(userActions.fetchUserRequest({ silent: true }))

      trackInternalEvent('generate_successfully', {
        section: ANALYTICS_SECTION_NAME,
        label: AI_WORK_EXPERIENCE_ANALYTICS_LABEL,
        description_empty: isDescriptionEmpty,
        mode: actionType,
      })

      if (isDescriptionEmpty) {
        yield put(
          actions.updateCard({
            sectionId: SectionNames.workExperiences,
            cardId: workExperienceId,
            values: { description: newDescription },
          }),
        )
        yield put(actions.setAiWorkExperienceEditorFieldAnimation(true))
        yield put(actions.setAiWorkExperienceResultContentAnimation(false))
      } else {
        yield put(actions.setIsAiWorkExperienceResultOpen(true))
      }
    }
  } catch (error) {
    yield put(actions.setGenerateAiWorkExperienceStatus(GenerateAiSummaryStatus.failed))
  }
}

export function* extractTextFromVoiceSaga({
  payload,
}: ReturnType<typeof actions.extractTextFromVoiceInput>) {
  const { audioBlob } = payload
  const { step }: QuestionnaireData = yield select(selectors.questionnaireData)
  const locale: string = yield select(selectors.iso_language_code)
  const isFutureGoalsStep = step === 6

  try {
    yield put(
      isFutureGoalsStep
        ? actions.setGoalsVoiceProcessingStatus(SpeechToTextStatus.processing)
        : actions.setAccomplishmentsVoiceProcessingStatus(SpeechToTextStatus.processing),
    )
    const formData = new FormData()
    const audioBlobFile = new Blob(audioBlob, { type: 'audio/mpeg-3' })
    formData.append('file', audioBlobFile)
    formData.append('locale', locale.substring(0, 2))

    const {
      data: { success, text },
    }: { data: { success: boolean; text: string } } = yield call(
      apiClient.post,
      `/ai-resume-draft/speech_to_text`,
      formData,
    )
    if (success) {
      yield put(
        isFutureGoalsStep
          ? actions.setFutureGoalsText(text)
          : actions.setPastAccomplishmentsText(text),
      )
      yield put(
        isFutureGoalsStep
          ? actions.generateResumeFromQuestionnaire()
          : actions.setQuestionnaireStep(step + 1),
      )
      yield call(performAfterPrefillAction, 'createWithAi')
    } else {
      yield put(
        isFutureGoalsStep
          ? actions.setGoalsVoiceInputError(VoiceInputErrors.unknown)
          : actions.setAccomplishmentsVoiceInputError(VoiceInputErrors.unknown),
      )
      trackInternalEvent(AIResumeDraftEvents.seeRecordingError, {
        label: isFutureGoalsStep
          ? AIResumeDraftEventLabels.careerGoals
          : AIResumeDraftEventLabels.professionalHighlights,
      })
    }
    const processingStatus = success ? SpeechToTextStatus.finished : SpeechToTextStatus.failed
    yield put(
      isFutureGoalsStep
        ? actions.setGoalsVoiceProcessingStatus(processingStatus)
        : actions.setAccomplishmentsVoiceProcessingStatus(processingStatus),
    )
  } catch (error) {
    trackInternalEvent(AIResumeDraftEvents.seeRecordingError, {
      label: isFutureGoalsStep
        ? AIResumeDraftEventLabels.careerGoals
        : AIResumeDraftEventLabels.professionalHighlights,
    })
    yield put(
      isFutureGoalsStep
        ? actions.setGoalsVoiceProcessingStatus(SpeechToTextStatus.failed)
        : actions.setAccomplishmentsVoiceProcessingStatus(SpeechToTextStatus.failed),
    )
    yield put(
      isFutureGoalsStep
        ? actions.setGoalsVoiceInputError(VoiceInputErrors.unknown)
        : actions.setAccomplishmentsVoiceInputError(VoiceInputErrors.unknown),
    )
  }
}

export function* fetchResumeExamplesSaga({
  payload: { resetPage } = {},
}: ReturnType<typeof actions.fetchResumeExamples>) {
  const perPage = 12
  const { currentPage, resumeExamples, categories, searchedTitle, selectedCategoryId }: UseExample =
    yield select(selectors.useExample)

  try {
    const pageNumber = resetPage ? 1 : currentPage + 1

    yield put(actions.fetchingResumeExamples(true))
    yield put(actions.setShowUseExamplePopup(true))

    if (pageNumber === 1) {
      yield put(
        actions.setResumeExamplesData({
          currentPage: pageNumber,
          resumeExamples: [],
          totalPages: 0,
        }),
      )
    }

    const searchRequest = call(apiClient.get, '/examples/search', {
      params: {
        page: pageNumber,
        per_page: perPage,
        job_title: searchedTitle || undefined,
        category_id: selectedCategoryId || undefined,
      },
    })

    const filtersRequest = call(apiClient.get, '/examples/filters')

    const [searchResponse, filtersResponse]: [
      AxiosResponse<{ items: ResumeExample[]; total: number }>,
      AxiosResponse<{ categories: BriefExampleCategory[]; jobTitles: string[] }> | undefined,
    ] = yield all([searchRequest, ...(!categories.length ? [filtersRequest] : [])])

    const {
      status,
      data: { items: examples, total },
    } = searchResponse

    const filters =
      filtersResponse?.status === 200
        ? {
            categories: [...filtersResponse.data.categories],
            jobTitles: [...filtersResponse.data.jobTitles],
          }
        : {}

    if (status === 200 && examples.length) {
      const formattedExamples = examples.map((example: ResumeExample) => {
        const { previewImageUrl } = example

        if (
          previewImageUrl.startsWith('https://s3.resume.io') ||
          previewImageUrl.startsWith('https://stagingbucket.resume.io')
        ) {
          return {
            ...example,
            compressedPreviewImageUrl: createImageUrlFormatter({
              base: previewImageUrl,
            })(previewImageUrl, {
              width: EXAMPLES_LIST_PREVIEW_IMAGE_WIDTH_PX * (devicePixelRatio || 1),
              format: 'auto',
            }),
          }
        }
        return {
          ...example,
          compressedPreviewImageUrl: '',
        }
      })

      yield put(
        actions.setResumeExamplesData({
          currentPage: pageNumber,
          totalPages: Math.ceil(total / perPage),
          resumeExamples:
            pageNumber === 1 ? formattedExamples : [...resumeExamples, ...formattedExamples],
          ...filters,
        }),
      )
      yield put(actions.setShowPrefillResumeModal(false))
    } else {
      // handle error
    }
  } catch (error) {
    // handle error
    ErrorLogger.log(error)
  } finally {
    yield put(actions.fetchingResumeExamples(false))
  }
}

export function* generateResumeFromExampleSaga({
  payload,
}: ReturnType<typeof actions.generateResumeFromExample>) {
  try {
    const { id }: Resume = yield select(selectors.resume)
    const {
      status,
      data: { success },
    } = yield call(apiClient.put, `resumes/${id}/use_example`, {
      exampleId: payload.exampleId,
    })

    if (status === 200 && success) {
      yield put(
        actions.fetchResumeRequest({
          id,
        }),
      )
      yield take(actions.fetchResumeSuccess)
      yield call(performAfterPrefillAction, 'useExample')
      yield put(actions.setShowUseExamplePopup(false))
      yield put(actions.setShowPrefillResumeModal(false))
      const { isAboveTablet } = matchMediaQueries()

      const builderTour: BuilderTourParams = yield select(uiSelectors.builderTour)
      if (!builderTour.isStartModalOpen) {
        yield put(
          actions.setHighlightedElement({
            content: i18n.t('builder.use_example.change_template_info'),
            target: `[data-highlight-id=${AFTER_USE_EXAMPLE_HIGHLIGHT_ID}]`,
            placement: isAboveTablet ? 'bottom' : 'top',
          }),
        )
      }
      trackInternalEvent(UseExampleEvents.prefillResumeSuccessfully, {
        label: UseExampleEventLabels.examplesPrefill,
        jobTitle: payload.jobTitle,
      })
      yield delay(5000)
      yield put(actions.setHighlightedElement(null))
    } else {
      // handle error
    }
  } catch (error) {
    // handle error
    ErrorLogger.log(error)
  } finally {
    yield put(actions.setShowUseExamplePopup(false))
    yield put(actions.setShowPrefillResumeModal(false))
  }
}

export function* highlightEmptyWorkExperienceSaga({
  payload,
}: ReturnType<typeof actions.highlightEmptyWorkExperience>) {
  try {
    const { delayMs } = payload

    if (delayMs) {
      yield delay(delayMs)
    }

    const { workExperiences }: Resume = yield select(selectors.resume)
    const { isPhone } = matchMediaQueries()
    let cardId: Optional<number> | string
    let emptyFieldName: ReturnType<typeof getEmptyFieldName> = 'title'

    if (workExperiences.length) {
      const card = workExperiences[0]
      cardId = card.cid || card.id
      emptyFieldName = getEmptyFieldName(SectionNames.workExperiences, card)
      yield put(actions.openCard({ id: cardId, sectionId: SectionNames.workExperiences }))
    } else {
      cardId = generateRandomId()
      yield put(
        actions.addCard({
          sectionName: SectionNames.workExperiences,
          cardId,
          options: { scrollIntoViewport: false, shouldOpen: true },
        }),
      )
    }

    // we need to wait until opening section item animation is done
    yield delay(300)

    const openCard: ResumeOpenedCard = yield select(selectors.openedCard)

    const highlightId = isPhone
      ? getCardFieldHighlightId(SectionNames.workExperiences, openCard?.id, emptyFieldName)
      : getCardHighlightId(SectionNames.workExperiences, openCard?.id)

    const targetSelector = `[data-highlight-id="${highlightId}"]`
    const targetElement = document.querySelector<HTMLElement>(targetSelector)

    if (targetElement) {
      if (targetElement.nodeName === 'INPUT') {
        targetElement.focus()
      } else {
        Array.from(targetElement.querySelectorAll('input'))
          .find(item => !isTruthy(item.value))
          ?.focus()
      }
    }

    yield put(
      actions.setHighlightedElement({
        ...payload,
        target: targetSelector,
        disableScrollParentFix: !isPhone,
        styles: joyRideSpotlightPositionInModalCssFix(targetElement),
      }),
    )

    trackInternalEvent('show_empty_work_experience_popup')

    // Wait until element closed
    const { payload: closeCallbackProps }: { payload: CallBackProps } = yield take(
      actions.closeHighlightedElement,
    )

    if (closeCallbackProps.origin === 'button_close') {
      trackInternalEvent('click_got_it', {
        label: 'empty_work_experience_popup',
      })
    }
  } catch (error) {
    ErrorLogger.log(error)
  }
}

export function* highlightWorkExperienceCardEmptyFieldSaga({
  payload,
}: ReturnType<typeof actions.highlightWorkExperienceCardEmptyField>) {
  try {
    const { tooltipData, cardId, emptyFieldName } = payload
    const { isPhone } = matchMediaQueries()

    // we need to wait until opening section item animation is done
    yield delay(300)

    const highlightId = getCardFieldHighlightId(
      SectionNames.workExperiences,
      cardId,
      emptyFieldName,
    )
    const targetSelector = `[data-highlight-id="${highlightId}"]`
    const targetElement = document.querySelector<HTMLElement>(targetSelector)

    if (targetElement) {
      if (targetElement.nodeName === 'INPUT') {
        targetElement.focus()
      } else {
        Array.from(targetElement.querySelectorAll('input'))
          .find(item => !isTruthy(item.value))
          ?.focus()
      }
    }

    yield put(
      actions.setHighlightedElement({
        ...tooltipData,
        target: targetSelector,
        disableScrollParentFix: !isPhone,
        styles: joyRideSpotlightPositionInModalCssFix(targetElement),
      }),
    )

    trackInternalEvent('show_empty_work_experience_popup')

    // Wait until element closed
    const { payload: closeCallbackProps }: { payload: CallBackProps } = yield take(
      actions.closeHighlightedElement,
    )

    if (closeCallbackProps.origin === 'button_close') {
      trackInternalEvent('click_got_it', {
        label: 'empty_work_experience_popup',
      })
    }
  } catch (error) {
    ErrorLogger.log(error)
  }
}

export function* autocloseWorkExperienceHighlightingSaga({
  payload,
}: ReturnType<typeof actions.updateCard>) {
  try {
    const highlightedElement: HighlightedElementData | null = yield select(
      selectors.highlightedElementData,
    )
    if (payload.sectionId === SectionNames.workExperiences && highlightedElement) {
      yield put(actions.setHighlightedElement(null))
    }
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function* autoTailorResumeQuietSaga({
  payload,
}: Omit<ReturnType<typeof actions.autoTailorResume>, 'type'>) {
  try {
    yield put(actions.setAutoTailoringFetchStatus(FetchStatuses.loading))

    const { resumeId, selectedKeywords, jobPostingId, fromPublicJobBoard } = payload

    const endpoint = fromPublicJobBoard
      ? `/tailored_resumes/auto_tailor_public`
      : `/tailored_resumes/auto_tailor`

    const {
      data: { tailoredResume },
    } = yield call(baseClient.post, endpoint, {
      resume_id: resumeId,
      job_posting_id: jobPostingId,
      keywords_selected: selectedKeywords,
      sections_to_tailor: SECTIONS_FOR_AUTO_TAILORING,
      skills_selected: [],
    })

    if (fromPublicJobBoard) {
      yield put(userActions.decrementAutoTailoringFromPublicJobBoardAttempts())
    } else {
      yield put(userActions.decrementAutoTailoringAttempts())
    }

    yield put(userActions.fetchUserRequest({ silent: true }))
    yield put(actions.setAutoTailoringFetchStatus(FetchStatuses.loaded))
    return tailoredResume
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.setAutoTailoringFetchStatus(FetchStatuses.failed))
  }
}

function* autoTailorResumeSaga({ payload }: ReturnType<typeof actions.autoTailorResume>) {
  try {
    yield put(actions.setAutoTailoredResume(null))
    yield put(actions.setAutoTailoringModalStep('auto_tailoring'))

    const tailoredResume: Resume = yield call(autoTailorResumeQuietSaga, { payload })
    const user: UserData = yield select(userSelectors.userData)
    const currentResume: Resume = yield select(selectors.resume)

    if (user.hasPremiumFeatures) {
      if (tailoredResume.intention === 'tailoring') {
        yield put(actions.fetchResumeSuccess(tailoredResume))
      }
      yield put(actions.setAutoTailoredResume(tailoredResume))
      yield delay(3000) // wait some time on auto-tailoring result screen
      yield put(actions.setOriginalResume(currentResume))
      navigate(`/resumes/${tailoredResume.id}/edit`)
      yield put(actions.setAutoTailoringModalStep(null))
      yield put(panelActions.fetchDocuments())
    } else {
      // workaround because free user doesn't create real resume in database
      const mockTailoredResume = { ...currentResume, ...tailoredResume, id: 0 }
      yield put(actions.setAutoTailoredResume(mockTailoredResume))
    }
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function getResumeNameForAutoTailoring(resume: Resume) {
  return [resume.firstName, resume.lastName, 'Resume'].filter(Boolean).join('_')
}

function* autoTailorResumeFromPublicJobBoardSaga() {
  try {
    const { mode, jobCardId, jobPostingId }: AutoTailoringFromPublicJobBoard = yield select(
      selectors.autoTailoringFromPublicJobBoard,
    )
    yield put(actions.setProcessingAutoTailoring(FetchStatuses.loading))

    yield put(
      actions.setAutoTailoringForPublicJobBoard({
        step: AutoTailoringFromPublicJobBoardStep.step2,
      }),
    )

    const currentResume: Resume = yield select(selectors.resume)

    yield put(
      actions.updateSimpleField({
        name: 'data',
        value: {
          stepByStepFlow: {
            currentStep: null,
          },
        },
        debounce: false,
      }),
    )
    yield take(actions.updateResumeSuccess)

    if (!currentResume.name || currentResume.name.trim() === '') {
      const name = getResumeNameForAutoTailoring(currentResume)

      yield put(
        actions.updateSimpleField({
          name: 'name',
          value: name,
          debounce: false,
        }),
      )
      yield take(actions.updateResumeSuccess)
    }

    yield call(fetchJobPostingData, { payload: { resumeId: currentResume?.id } })

    const keywords: string[] = yield select(selectors.addedKeywordsForAutoTailoring)
    const jobPostingDataForAutoTailoring: ResumeScoreAPIData = yield select(
      selectors.jobPostingDataForAutoTailoring,
    )
    const user: UserData = yield select(userSelectors.userData)

    const tailoredResume: Resume = yield call(autoTailorResumeQuietSaga, {
      payload: {
        resumeId: currentResume.id,
        selectedKeywords: keywords,
        jobPostingId: jobPostingDataForAutoTailoring.jobPostingId,
        fromPublicJobBoard: !user.hasPremiumFeatures,
      },
    })

    yield put(
      actions.setAutoTailoringForPublicJobBoard({
        jobCardId: currentResume.jobCardId,
      }),
    )

    if (mode === AutoTailoringMode.fromPublicJB) {
      yield call(updateResumeOptimizerDataSaga, {
        payload: {
          resumeId: currentResume.id,
          jobPostingId: jobPostingDataForAutoTailoring.jobPostingId,
        },
      })

      yield call(baseClient.post, '/job-postings/update_selected_resume', {
        user_id: user.id,
        resume_id: tailoredResume.id,
        job_posting_id: currentResume.jobPostingId,
        job_card_id: currentResume.jobCardId,
      })
    }

    yield put(actions.setProcessingAutoTailoring(FetchStatuses.loaded))

    const searchParams = createAutoTailoringUrlParams({
      mode,
      autotailoringStep: AutoTailoringFromPublicJobBoardStep.step2,
      jobCardId: currentResume.jobCardId || jobCardId,
      jobPostingId,
    })

    navigate(`/resumes/${tailoredResume.id}/edit?${searchParams.toString()}`)
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.setProcessingAutoTailoring(FetchStatuses.failed))
  }
}

function* skipAutoTailorForJobBoardSaga({
  payload,
}: ReturnType<typeof actions.skipAutoTailorForJobBoard>) {
  try {
    yield put(
      actions.setAutoTailoringForPublicJobBoard({
        processingAutoTailoring: FetchStatuses.loading,
      }),
    )

    const { tailoredResumeId, mode, jobCardId, shouldRemoveAutoTailoredResume } = payload
    const { data: tailoredResume }: AxiosResponse<Resume> = yield call(
      apiClient.get,
      `/resumes/${tailoredResumeId}`,
    )

    if (tailoredResume.autoTailored && tailoredResume.originalResumeId) {
      const { data: originalResume }: AxiosResponse<Resume> = yield call(
        apiClient.get,
        `/resumes/${tailoredResume.originalResumeId}`,
      )
      const user: UserData = yield select(userSelectors.userData)

      yield call(baseClient.post, '/job-postings/update_selected_resume', {
        user_id: user.id,
        resume_id: tailoredResume.originalResumeId,
        job_posting_id: tailoredResume.jobPostingId,
        job_card_id: tailoredResume.jobCardId || jobCardId,
      })

      yield call(baseClient.post, `/tailored_resumes/sync_resume_with_job_posting`, null, {
        params: {
          job_posting_id: tailoredResume.jobPostingId,
          resume_id: tailoredResume.originalResumeId,
          average_score: originalResume.averageScore,
        },
      })

      if (shouldRemoveAutoTailoredResume) {
        yield call(apiClient.delete, `/resumes/${tailoredResume.id}`)
      } else {
        yield call(baseClient.patch, `/tailored_resumes/${tailoredResume.id}/approve`)
      }
    }

    const searchParams = createAutoTailoringUrlParams({
      mode,
      autotailoringStep: AutoTailoringFromPublicJobBoardStep.finished,
    })

    const targetResumeId =
      (tailoredResume.autoTailored && tailoredResume.originalResumeId) || tailoredResume.id

    navigate(`/resumes/${targetResumeId}/edit?${searchParams.toString()}`)

    yield put(
      actions.setAutoTailoringForPublicJobBoard({
        processingAutoTailoring: FetchStatuses.loaded,
      }),
    )
  } catch (error) {
    yield put(
      actions.setAutoTailoringForPublicJobBoard({
        processingAutoTailoring: FetchStatuses.failed,
      }),
    )
    ErrorLogger.log(error)
  }
}

function* cancelAutoTailorForMemberJobBoardSaga({
  payload,
}: ReturnType<typeof actions.cancelAutoTailorForMemberJobBoard>) {
  try {
    const { tailoredResumeId } = payload

    yield fork(apiClient.delete, `/resumes/${tailoredResumeId}`)

    navigate(`/job-search`)
  } catch (error) {
    ErrorLogger.log(error)
  }
}

function* autoTailorAfterPaymentSaga({
  payload,
}: ReturnType<typeof actions.autoTailorAfterPayment>) {
  try {
    yield put(actions.setAutoTailoredResume(null))

    const tailoredResume: Resume = yield call(autoTailorResumeQuietSaga, { payload })

    yield put(actions.setAutoTailoredResume(tailoredResume))

    navigate(`/resumes/${tailoredResume.id}/edit`)
  } catch (error) {
    ErrorLogger.log(error)
    navigate(`/resumes/${payload.resumeId}/edit`)
  }
}

function* approveResumeSaga() {
  try {
    yield put(actions.setApproveAutoTailoredResumeStatus(FetchStatuses.loading))
    const resume: Resume = yield select(selectors.resume)
    yield call(baseClient.patch, `/tailored_resumes/${resume.id}/approve`)
    yield put(actions.approveResumeSuccess())
    const redirectURL = () => {
      const { intention, jobCardId } = resume
      if (intention === 'tailoring' && !!jobCardId) {
        return `/job-tracking?card_id=${jobCardId}`
      }
      return '/?tab=resume&autotailored=true&scrollTo=documents'
    }
    navigate(redirectURL())
    yield put(renderingActions.showRDUpsellGetStartedModal())
    yield put(actions.setApproveAutoTailoredResumeStatus(FetchStatuses.loaded))
    yield put(panelActions.fetchDocuments())
  } catch (error) {
    ErrorLogger.log(error)
    yield put(actions.setApproveAutoTailoredResumeStatus(FetchStatuses.failed))
  }
}

function* approveResumeForJobBoardFlowSaga({
  payload,
}: ReturnType<typeof actions.approveResumeForJobBoardFlow>) {
  const { resumeId, handleSuccessRedirect, jobCardId } = payload
  try {
    yield put(
      actions.setAutoTailoringForPublicJobBoard({
        processingAutoTailoring: FetchStatuses.loading,
      }),
    )

    const user: UserData = yield select(userSelectors.userData)

    let resume: Resume | null = yield select(selectors.resume)

    if (!resume || resume.id !== resumeId) {
      const { data: targetResume }: AxiosResponse<Resume> = yield call(
        apiClient.get,
        `/resumes/${resumeId}`,
      )
      resume = targetResume
    }

    if (!resume.name || resume.name.trim() === '') {
      const name = getResumeNameForAutoTailoring(resume)
      yield call(apiClient.patch, `/resumes/${resume.id}`, { ...resume, name })
    }

    yield call(baseClient.patch, `/tailored_resumes/${resumeId}/approve`)
    yield call(baseClient.post, '/job-postings/update_selected_resume', {
      user_id: user.id,
      resume_id: resume.id,
      job_posting_id: resume.jobPostingId,
      job_card_id: resume.jobCardId || jobCardId,
    })

    resume = yield select(selectors.resume)

    if (resume && resume.id === resumeId) {
      yield put(actions.approveResumeSuccess({ jobCardId: resume.jobCardId || jobCardId }))
    }

    if (handleSuccessRedirect) {
      handleSuccessRedirect()
    } else {
      yield put(
        actions.setAutoTailoringForPublicJobBoard({
          processingAutoTailoring: FetchStatuses.loaded,
        }),
      )
    }
  } catch (error) {
    yield put(
      actions.setAutoTailoringForPublicJobBoard({ processingAutoTailoring: FetchStatuses.failed }),
    )
    ErrorLogger.log(error)
  }
}

function* workExperienceTailorBannerTriggerSaga() {
  const { bannerTriggeredFrom }: ResumeOptimizerSection = yield select(selectors.optimizerSection)
  const resume: Resume = yield select(selectors.resume)

  if (
    resume.skipOptimizerBanner ||
    (bannerTriggeredFrom && bannerTriggeredFrom !== SectionNames.workExperiences)
  ) {
    return
  }

  const isWorkExperienceEmpty = isEmptySection(resume, SectionNames.workExperiences)

  if (isWorkExperienceEmpty) {
    yield put(actions.setOptimizerBannerTriggeredFrom(null))
  } else {
    yield put(actions.setOptimizerBannerTriggeredFrom(SectionNames.workExperiences))
  }
}

function* watchWorkExperienceTailorBannerTriggerSaga({
  payload,
}: ReturnType<typeof actions.addCard | typeof actions.updateCard | typeof actions.deleteCard>) {
  const sectionId = 'sectionName' in payload ? payload.sectionName : payload.sectionId
  if (sectionId !== SectionNames.workExperiences) {
    return
  }

  yield call(workExperienceTailorBannerTriggerSaga)
}

export function* fetchCreateResumeWithLinkedInProfileSaga({
  payload,
}: ReturnType<typeof actions.fetchCreateResumeWithLinkedInProfile>) {
  const { linkedInProfileURL } = payload
  try {
    yield put(
      actions.updateCreateResumeWithLinkedInState({
        fetchLinkedInProfileStatus: FetchStatuses.loading,
      }),
    )
    const { status, data } = yield call(
      apiClient.get,
      `/linkedin-data-profile/profile?linkedin_profile=${linkedInProfileURL}`,
    )

    if (status === 200 && !isEmpty(data)) {
      yield put(
        actions.updateCreateResumeWithLinkedInState({
          fetchLinkedInProfileStatus: FetchStatuses.loaded,
          errorState: false,
          linkedInProfileData: data,
          currentStep: CreateResumeWithLinkedInSteps.linkedInProfile,
        }),
      )
    } else {
      // handle error
      yield put(
        actions.updateCreateResumeWithLinkedInState({
          fetchLinkedInProfileStatus: FetchStatuses.failed,
        }),
      )
    }
  } catch (error) {
    // handle error
    yield put(
      actions.updateCreateResumeWithLinkedInState({
        fetchLinkedInProfileStatus: FetchStatuses.failed,
        errorState: true,
      }),
    )

    if (axios.isAxiosError(error) && error.response?.status === 404) {
      const axiosError = error as AxiosError
      axiosError.message = `${axiosError.message}. The requested resume might not exist.`
      ErrorLogger.log(axiosError)
    } else ErrorLogger.log(error)
  } finally {
    yield put(
      actions.updateCreateResumeWithLinkedInState({
        fetchLinkedInProfileStatus: FetchStatuses.notAsked,
      }),
    )
  }
}

export function* updateResumeWithLinkedInProfileSaga({
  payload,
}: ReturnType<typeof actions.updateResumeWithLinkedInProfile>) {
  const { linkedInProfileURL, resumeId } = payload
  try {
    yield put(
      actions.updateCreateResumeWithLinkedInState({
        updateResumeWithLinkedInStatus: FetchStatuses.loading,
      }),
    )
    const { status, data } = yield call(
      apiClient.patch,
      `/linkedin-data-profile/prefill-resume?linkedin_profile=${linkedInProfileURL}` +
        `&resume_id=${resumeId}`,
    )
    if (status === 200 && !isEmpty(data)) {
      yield put(
        actions.updateCreateResumeWithLinkedInState({
          fetchLinkedInProfileStatus: FetchStatuses.loaded,
        }),
      )
      yield put(actions.resetCreateResumeWithLinkedInFlow())
      yield put(
        actions.fetchResumeRequest({
          id: resumeId,
        }),
      )
      yield take(actions.fetchResumeSuccess)
      yield call(performAfterPrefillAction, 'createWithLinkedIn')
      yield put(actions.setShowPrefillResumeModal(false))
      trackInternalEvent('prefill_resume_successfully', {
        label: 'li_prefill',
      })
    } else {
      // handle error
      yield put(
        actions.updateCreateResumeWithLinkedInState({
          updateResumeWithLinkedInStatus: FetchStatuses.failed,
        }),
      )
    }
  } catch (error) {
    // handle error
    yield put(
      actions.updateCreateResumeWithLinkedInState({
        errorState: true,
      }),
    )
    yield put(
      actions.updateCreateResumeWithLinkedInState({
        updateResumeWithLinkedInStatus: FetchStatuses.failed,
      }),
    )
    ErrorLogger.log(error)
  } finally {
    yield put(
      actions.updateCreateResumeWithLinkedInState({
        updateResumeWithLinkedInStatus: FetchStatuses.notAsked,
      }),
    )
  }
}

export function* startAiResumeReviewSaga() {
  const resume: Resume = yield select(selectors.resume)
  const user: UserData = yield select(userSelectors.userData)
  try {
    trackInternalEvent('start_ai_review')
    yield put(actions.setAiResumeReviewStatus(FetchStatuses.loading))
    const { status, data } = yield call(
      apiClient.post,
      `/resumes/${resume?.id}/ai-resume-full-review`,
    )
    if (status === 200 && !isEmpty(data)) {
      yield put(actions.setAiResumeReviewResult(data))
      yield put(actions.setAiResumeReviewStatus(FetchStatuses.loaded))
      if (!user.hasPremiumFeatures) {
        yield put(userActions.decrementAiResumeReviewAttempts())
      }
      trackInternalEvent('generate_ai_review_successfully', { score: data?.scoreHeadline })
    } else {
      yield put(actions.setAiResumeReviewStatus(FetchStatuses.failed))
      trackInternalEvent('see_ai_review_error')
    }
  } catch (error) {
    yield put(actions.setAiResumeReviewStatus(FetchStatuses.failed))
    trackInternalEvent('see_ai_review_error')
    ErrorLogger.log(error)
  }
}

export function* getLatestAiResumeReviewSaga() {
  const resume: Resume = yield select(selectors.resume)
  try {
    yield put(actions.setAiResumeReviewStatus(FetchStatuses.loading))
    const { status, data } = yield call(
      apiClient.get,
      `/resumes/${resume?.id}/ai-resume-full-review/last-review`,
    )

    if (status === 200 && !isEmpty(data)) {
      yield put(actions.setAiResumeReviewResult(data))
      yield put(actions.setAiResumeReviewStatus(FetchStatuses.loaded))
    } else {
      yield put(actions.setAiResumeReviewResult(null))
      yield put(actions.setAiResumeReviewStatus(FetchStatuses.notAsked))
    }
  } catch (error) {
    yield put(actions.setAiResumeReviewStatus(FetchStatuses.failed))
    ErrorLogger.log(error)
  }
}

function* initAutoTailoringFromMemberJobBoardSaga({
  payload,
}: ReturnType<typeof actions.initAutoTailoringFromMemberJobBoard>) {
  const { jobPostingId } = payload

  let resume: Resume = yield select(selectors.resume)

  if (!resume) {
    yield take(actions.fetchResumeSuccess)
    resume = yield select(selectors.resume)
  }

  const isDataMissed = isDataMissedForAutoTailoring(resume)

  const createJobPostingResponse: AxiosResponse<CreateJobPostingResponse> = yield call(
    baseClient.get,
    `/job-postings/${jobPostingId}`,
  )

  const {
    data: {
      jobPosting: { jobTitle, companyName, keywords },
    },
  } = createJobPostingResponse

  const apiResponse = {
    employerName: companyName,
    suggestedJobTitle: jobTitle,
    averageScore: 0,
    jobPostingId,
    keywords: keywords || [],
  }

  yield put(actions.setResumeScoreAPIResponse(apiResponse))
  yield put(actions.setJobPostingDataForAutoTailoring(apiResponse))

  const userHasPremiumAccess: boolean = yield select(userSelectors.premiumAccess)

  if (userHasPremiumAccess && !resume.showPrefillResumeModal) {
    if (isDataMissed) {
      yield put(actions.setOpenOptimizerModal(true))
    } else {
      yield put(actions.setAutoTailoringModalStep('keywords_selection'))
    }
  }
}

// Export
export const resumeEditorSagas = function* resumeEditorSaga() {
  yield all([
    takeEvery(actions.fetchResumeRequest, fetchResumeSaga),
    takeEvery(actions.updateSimpleField, startResumeUpdateSaga),
    takeEvery(actions.updateTemplate, updateTemplateSaga),
    takeEvery(actions.updateCard, startResumeUpdateSaga),
    takeEvery(actions.addCard, addCardSaga),
    takeEvery(actions.addCardSubItem, startResumeUpdateSaga),
    takeEvery(actions.moveCardSubItem, startResumeUpdateSaga),
    takeEvery(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      actions.addCustomSection as any,
      createInterceptor(needOnline as InterceptionHelper),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      addCustomSectionSaga as any,
    ),
    takeEvery(actions.deleteCard, startResumeUpdateSaga),
    takeEvery(actions.moveCard, startResumeUpdateSaga),
    takeEvery(actions.moveSection, startResumeUpdateSaga),
    takeEvery(actions.renameSection, startResumeUpdateSaga),
    takeEvery(actions.prefillResume, prefillResumeSaga),
    takeLatest(actions.deleteSection, startResumeUpdateSaga),
    takeLatest(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      actions.uploadPhoto as any,
      createInterceptor(needOnline as InterceptionHelper),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      uploadImageSaga as any,
    ),
    takeLatest(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      actions.deletePhoto as any,
      createInterceptor(needOnline as InterceptionHelper),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      deleteImageSaga as any,
    ),
    takeLatest(actions.startResumeUpdate, updateResumeSaga),
    takeLatest(initActions.setInternetConnectionStatus, internetConnectionStatusChangedSaga),
    takeLatest(actions.startResumeOptimizerProcess, startResumeOptimizerProcessSaga),
    takeLatest(actions.resetResumeOptimizerProcess, startResumeOptimizerProcessSaga),
    takeLatest(actions.checkResumeScore, checkResumeScoreSaga),
    takeLatest(actions.deleteJobPosting, deleteJobPostingSaga),
    takeLatest(actions.editJobDetails, editJobDetailsSaga),
    takeLatest(actions.generateTailoredAICoverLetter, aiCoverLetterTailoredGenerateSaga),
    takeLatest(actions.startImprovingResume, startImprovingResumeSaga),
    takeLatest(
      actions.startImprovingResumeForEditorWithTabs,
      startImprovingResumeForEditorWithTabsSaga,
    ),
    takeLatest(actions.parseAndSavePrefilledResume, parseAndSavePrefilledResumeSaga),
    takeLatest(actions.generateAICVUntailored, generateAICVUntailoredSaga),
    takeLatest(actions.fetchLinkedInProfile, fetchLinkedInProfileSaga),
    takeLatest(actions.generateResumeFromLinkedIn, generateResumeFromLinkedInSaga),
    takeLatest(actions.generateResumeFromQuestionnaire, generateResumeFromQuestionnaireSaga),
    takeLatest(actions.generateResumeAiProfileSummary, generateResumeAiProfileSummarySaga),
    takeLatest(actions.extractTextFromVoiceInput, extractTextFromVoiceSaga),
    takeLatest(actions.fetchResumeExamples, fetchResumeExamplesSaga),
    takeLatest(actions.generateResumeFromExample, generateResumeFromExampleSaga),
    takeLatest(actions.updateResumeOptimizerData, updateResumeOptimizerDataSaga),
    takeLatest(actions.highlightEmptyWorkExperience, highlightEmptyWorkExperienceSaga),
    takeLatest(
      actions.highlightWorkExperienceCardEmptyField,
      highlightWorkExperienceCardEmptyFieldSaga,
    ),
    takeLatest(actions.updateCard, autocloseWorkExperienceHighlightingSaga),
    takeLatest(actions.autoTailorResume, autoTailorResumeSaga),
    takeLatest(actions.autoTailorAfterPayment, autoTailorAfterPaymentSaga),
    takeLatest(actions.approveResume, approveResumeSaga),
    takeLatest(actions.fetchResumeSuccess, workExperienceTailorBannerTriggerSaga),
    takeLatest(
      [actions.addCard, actions.updateCard, actions.deleteCard],
      watchWorkExperienceTailorBannerTriggerSaga,
    ),
    takeLatest(actions.generateResumeAiWorkExperience, generateResumeAiWorkExperienceSaga),
    takeLatest(
      actions.fetchCreateResumeWithLinkedInProfile,
      fetchCreateResumeWithLinkedInProfileSaga,
    ),
    takeLatest(actions.updateResumeWithLinkedInProfile, updateResumeWithLinkedInProfileSaga),
    takeLatest(actions.fetchJobPostingData, fetchJobPostingData),
    takeLatest(actions.startAiResumeReview, startAiResumeReviewSaga),
    takeLatest(actions.getLatestAiResumeReview, getLatestAiResumeReviewSaga),
    takeLatest(actions.autoTailorForPublicJobBoard, autoTailorResumeFromPublicJobBoardSaga),
    takeLatest(actions.approveResumeForJobBoardFlow, approveResumeForJobBoardFlowSaga),
    takeLatest(actions.skipAutoTailorForJobBoard, skipAutoTailorForJobBoardSaga),
    takeLatest(actions.cancelAutoTailorForMemberJobBoard, cancelAutoTailorForMemberJobBoardSaga),
    takeLatest(
      actions.initAutoTailoringFromMemberJobBoard,
      initAutoTailoringFromMemberJobBoardSaga,
    ),
  ])
}
